import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import { Alert, Button, Card, Col, Divider, Modal, Popover, Row, Space, Statistic, Tag, Typography } from 'antd';
import { MinusOutlined, PlusOutlined, DollarOutlined, WalletOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import { CheckboxField, TextField } from '@core/form';
import { AntForm, SimpleTable } from '@core/ui';

import { IHandoverDeclarationDto } from '../dto';
import { useHandoverDeclarations } from '../hooks';
import { HandoverDeclarationsContext } from '../context';
import { IHandoverDeclarationsDetails } from '../interfaces';
import { GetHandoverDeclarationsDetailsRepoType, IGetHandoverDeclarationsDetailsRepo } from '../repos';
import { useService } from '@core/inversify-react';
import { CurrencySymbols } from '@infra/utils';

// ========== Yardımçı Komponentlər ==========

interface PackageCounterProps {
  label: string;
  name: string;
  value: string;
  onIncrement: () => void;
  onDecrement: () => void;
}

const PackageCounter: FC<PackageCounterProps> = ({ label, name, value, onIncrement, onDecrement }) => (
  <Row align='middle' gutter={8} style={{ marginBottom: 8 }}>
    <Col flex='60px'>
      <Typography.Text strong style={{ fontSize: 13 }}>
        {label}:
      </Typography.Text>
    </Col>
    <Col flex='auto'>
      <Space size={0} style={{ width: '100%', display: 'flex' }}>
        <Button size='small' icon={<MinusOutlined />} onClick={onDecrement} disabled={parseInt(value) <= 0} style={{ borderRadius: '4px 0 0 4px' }} />
        <div style={{ flex: 1 }}>
          <TextField
            name={name}
            format='decimal'
            item={{ style: { margin: 0 } }}
            input={{
              placeholder: `${label}`,
              max: 20,
              min: 0,
              size: 'small',
              style: { textAlign: 'center', borderRadius: 0 },
            }}
          />
        </div>
        <Button size='small' icon={<PlusOutlined />} onClick={onIncrement} disabled={parseInt(value) >= 20} style={{ borderRadius: '0 4px 4px 0' }} />
      </Space>
    </Col>
  </Row>
);

interface PaymentSummaryRowProps {
  title: string;
  foreignAmount?: number;
  foreignCurrency?: string;
  aznAmount: number;
  isTotal?: boolean;
  isClickable?: boolean;
  onAmountClick?: () => void;
  infoContent?: React.ReactNode;
}

const PaymentSummaryRow: FC<PaymentSummaryRowProps> = ({ title, foreignAmount, foreignCurrency, aznAmount, isTotal = false, isClickable = false, onAmountClick, infoContent }) => (
  <tr style={isTotal ? { fontWeight: 600, backgroundColor: '#fafafa' } : undefined}>
    <th style={{ padding: '8px 12px' }}>
      <Space size={4}>
        {title}
        {infoContent && (
          <Popover content={infoContent} trigger='hover' placement='right'>
            <QuestionCircleOutlined style={{ fontSize: 12, color: '#1890ff', cursor: 'pointer' }} />
          </Popover>
        )}
      </Space>
    </th>
    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{foreignAmount !== undefined && `${foreignAmount.toFixed(2)} ${foreignCurrency}`}</td>
    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
      {isClickable ? (
        <Button type='link' size='small' onClick={onAmountClick} style={{ padding: 0, height: 'auto', fontWeight: isTotal ? 600 : 400 }}>
          {aznAmount.toFixed(2)} {CurrencySymbols.AZN}
        </Button>
      ) : (
        `${aznAmount.toFixed(2)} ${CurrencySymbols.AZN}`
      )}
    </td>
  </tr>
);

const FormikComponent: FC<FormikProps<IHandoverDeclarationDto>> = ({ handleSubmit, isSubmitting, values, setFieldValue }) => {
  const { ids, onClose } = useContext(HandoverDeclarationsContext);
  const [sumSyncAmount, setSumSyncAmount] = useState<number>(parseFloat(values.cashAmount));

  const getHandoverDeclarationDetailsRepo = useService<IGetHandoverDeclarationsDetailsRepo>(GetHandoverDeclarationsDetailsRepoType);

  const details = useQuery<IHandoverDeclarationsDetails, Error, IHandoverDeclarationsDetails, [string, string[], string, string, string, string, string]>(
    ['declarationshandover', ids, 'handover', 'details', values.medium_package, values.small_package, values.big_package],
    async ({ queryKey }) => {
      const [, ids] = queryKey;
      const result = await getHandoverDeclarationDetailsRepo.execute({ ids, body: { medium_package: values.medium_package, small_package: values.small_package, big_package: values.big_package } });
      if (result.status === 200) {
        return result.response;
      } else {
        throw new Error(result.response);
      }
    },
  );

  const onIncrement = useCallback(
    (field: string) => {
      let packagePrice = parseInt(values[field]) || 0;
      if (packagePrice < 20) packagePrice++;
      setFieldValue(field, packagePrice.toString());
    },
    [values, setFieldValue],
  );

  const onDecrement = useCallback(
    (field: string) => {
      let packagePrice = parseInt(values[field]) || 0;
      if (packagePrice > 0) packagePrice--;
      setFieldValue(field, packagePrice.toString());
    },
    [values, setFieldValue],
  );

  useEffect(() => {
    const terminalAmount = parseFloat(values.terminalAmount) || 0;
    const cashAmount = Math.round((sumSyncAmount - terminalAmount) * 100) / 100;

    if (cashAmount < 0) {
      setFieldValue('cashAmount', '0');
    } else {
      setFieldValue('cashAmount', cashAmount.toString());
    }

    if (terminalAmount > sumSyncAmount) {
      setFieldValue('terminalAmount', sumSyncAmount.toString());
    } else if (terminalAmount < 0) {
      setFieldValue('terminalAmount', '');
    }
  }, [setFieldValue, sumSyncAmount, values.terminalAmount]);

  useEffect(() => {
    if (values.handoverPreparations) {
      // Anbara yönəlt checked olarsa, Təhvil ver unchecked olsun
      setFieldValue('handover', false);
    } else {
      // Anbara yönəlt unchecked olarsa, Təhvil ver checked olsun
      setFieldValue('handover', true);
    }
  }, [setFieldValue, values.handoverPreparations]);

  useEffect(() => {
    if (details.data) {
      setSumSyncAmount(details.data.debt.all.azn);
    }
  }, [details.data]);

  const amountToBeGivenBack = useMemo(() => {
    if (details.data && details.data.debt.all.azn && values.amountToBeBorrowed.length) {
      let borrowAmount = parseFloat(values.amountToBeBorrowed);
      if (isNaN(borrowAmount)) borrowAmount = 0;
      const amount = borrowAmount - details.data.debt.all.azn;
      if (amount > 0) return { color: 'green', amount };
      if (amount < 0) return { color: 'red', amount };
      return { color: 'black', amount };
    } else {
      return { color: 'black', amount: 0 };
    }
  }, [details, values.amountToBeBorrowed]);

  const title = useMemo(
    () => (
      <Space size={12} align='center'>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Bağlamaları təhvil ver
        </Typography.Title>
        {values.debt.total.usd > 0.03 && (
          <Tag color='error' icon={<WalletOutlined />}>
            Ödəniş tələb edilir
          </Tag>
        )}
      </Space>
    ),
    [values],
  );

  if (!details) {
    return null;
  }

  return (
    <Modal
      visible={true}
      width={750}
      title={title}
      onOk={() => handleSubmit()}
      confirmLoading={isSubmitting}
      onCancel={onClose}
      okText='Təsdiq et'
      cancelText='İmtina'
      bodyStyle={{ maxHeight: '75vh', overflowY: 'auto', padding: '6px' }}
    >
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        {/* Ödəniş Tələbi Xəbərdarlığı */}
        {values.debt.total.usd > 0.03 && (
          <Alert
            message={
              <Space size={8} style={{ width: '100%', justifyContent: 'center' }}>
                <WalletOutlined style={{ fontSize: 20 }} />
                <Typography.Text strong style={{ fontSize: 16 }}>
                  DİQQƏT! ÖDƏNIŞ TƏLƏB EDİLİR
                </Typography.Text>
              </Space>
            }
            description={
              <div style={{ textAlign: 'center' }}>
                <Typography.Text>
                  Bu bağlamaları ödənişsiz təhvil verməyin! Müştəri{' '}
                  <strong style={{ fontSize: 15 }}>
                    {values.debt.total.usd.toFixed(2)} {CurrencySymbols.USD}
                  </strong>{' '}
                  ({values.debt.total.azn.toFixed(2)} {CurrencySymbols.AZN}) borcludur.
                </Typography.Text>
              </div>
            }
            type='error'
            showIcon
            banner
            style={{
              marginBottom: 12,
              border: '2px solid #ff4d4f',
              boxShadow: '0 4px 12px rgba(255, 77, 79, 0.3)',
            }}
          />
        )}

        {/* Ödəniş Xülasəsi və Balans - Yan-yana */}
        <Row gutter={12}>
          <Col xs={24} lg={14}>
            <Card
              size='small'
              title={
                <Typography.Text strong style={{ fontSize: 14 }}>
                  Ödəniş Xülasəsi
                </Typography.Text>
              }
              bodyStyle={{ padding: '8px 12px' }}
            >
              <SimpleTable>
                <tbody>
                  <PaymentSummaryRow
                    title='Sifariş'
                    foreignAmount={details.data?.ordersPrice.try || values?.ordersPrice.try}
                    foreignCurrency={CurrencySymbols.TRY}
                    aznAmount={details.data?.ordersPrice.azn || values?.ordersPrice.azn}
                  />
                  <PaymentSummaryRow
                    title='Bağlama'
                    foreignAmount={details.data?.deliveryPrice.usd || values?.deliveryPrice.usd}
                    foreignCurrency={CurrencySymbols.USD}
                    aznAmount={details.data?.deliveryPrice.azn || values?.deliveryPrice.azn}
                  />
                  <PaymentSummaryRow
                    title='Borc'
                    foreignAmount={details.data?.debt.total.usd || values?.debt.total.usd}
                    foreignCurrency={CurrencySymbols.USD}
                    aznAmount={details.data?.debt.total.azn || values?.debt.total.azn}
                  />
                  <PaymentSummaryRow title='Paket qiyməti' aznAmount={details.data?.debt.all.package || values?.debt.all.package} />
                  <tr>
                    <td colSpan={3}>
                      <Divider style={{ margin: '4px 0' }} />
                    </td>
                  </tr>
                  <PaymentSummaryRow
                    title='Cəmi ödəniləcək'
                    aznAmount={details.data?.debt.all.azn || values?.debt.all.azn}
                    isTotal
                    isClickable
                    onAmountClick={() => setSumSyncAmount(details.data?.debt.all.azn! || values?.debt.all.azn)}
                    infoContent={
                      <div style={{ maxWidth: 280 }}>
                        <Typography.Paragraph style={{ marginBottom: 8 }}>
                          <strong>Cəmi ödəniləcək</strong> məbləği cari seçilmiş bağlamaları və borcları daxil olmaqla toplam ödəməli olduğunuz məbləği göstərir.
                        </Typography.Paragraph>
                        <Typography.Paragraph style={{ marginBottom: 0, padding: '6px', backgroundColor: '#f0f5ff', borderRadius: 4, fontSize: 12 }}>
                          💡 <strong>Məbləğin üzərinə klikləyin</strong> - avtomatik olaraq "Nağd" inputuna düşəcək və istifadəçidən tələb edəcəyiniz məbləğ olacaq.
                        </Typography.Paragraph>
                      </div>
                    }
                  />
                  <PaymentSummaryRow
                    title='Minimal ödəniləcək'
                    aznAmount={details.data?.debt.minimum.azn || values?.debt.minimum.azn}
                    isClickable
                    onAmountClick={() => setSumSyncAmount(details.data?.debt.minimum.azn! || values?.debt.minimum.azn!)}
                    infoContent={
                      <div style={{ maxWidth: 280 }}>
                        <Typography.Paragraph style={{ marginBottom: 8 }}>
                          <strong>Minimal ödəniləcək</strong> məbləği müştərinin balansını da nəzərə almaqla minimal ödəyə biləcəyi məbləği göstərir.
                        </Typography.Paragraph>
                        <Typography.Paragraph style={{ marginBottom: 0, padding: '6px', backgroundColor: '#f0f5ff', borderRadius: 4, fontSize: 12 }}>
                          💡 <strong>Məbləğin üzərinə klikləyin</strong> - avtomatik olaraq "Nağd" inputuna düşəcək və istifadəçidən tələb edəcəyiniz məbləğ olacaq.
                        </Typography.Paragraph>
                      </div>
                    }
                  />
                </tbody>
              </SimpleTable>
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Card
              size='small'
              title={
                <Space size={4}>
                  <DollarOutlined />
                  <Typography.Text strong style={{ fontSize: 14 }}>
                    Balans
                  </Typography.Text>
                </Space>
              }
              bodyStyle={{ padding: '12px' }}
            >
              <Space direction='vertical' size={8} style={{ width: '100%' }}>
                <div style={{ backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                  <Statistic title='ABŞ Dolları' value={details.data?.balance.usd || values?.balance.usd} precision={2} prefix='$' valueStyle={{ color: '#3f8600', fontSize: 20 }} />
                </div>
                <div style={{ backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                  <Statistic title='Türk Lirəsi' value={details.data?.balance.try || values?.balance.try} precision={2} prefix='₺' valueStyle={{ color: '#cf1322', fontSize: 20 }} />
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Ödəniş Forması */}
        <AntForm layout='vertical'>
          <Card
            size='small'
            title={
              <Space size={8}>
                <Typography.Text strong style={{ fontSize: 14 }}>
                  Ödəniş Detalları
                </Typography.Text>
                <Popover
                  content={
                    <div style={{ maxWidth: 350 }}>
                      <Typography.Title level={5} style={{ marginBottom: 12 }}>
                        💳 Terminal ödənişi
                      </Typography.Title>
                      <Typography.Paragraph style={{ marginBottom: 12 }}>
                        <strong>Terminal</strong> inputuna daxil etdiyiniz məbləğ terminaldan ödəniləcək məbləği göstərir. Ödənilməli əsas məbləğdən terminalda nə qədər ödənirsə, qalıq{' '}
                        <strong>Nağd</strong> inputunda avtomatik hesablanır.
                      </Typography.Paragraph>
                      <Typography.Paragraph style={{ marginBottom: 12, padding: '8px', backgroundColor: '#e6f7ff', borderRadius: 4 }}>
                        <strong>Məsələn:</strong> Əgər bütün məbləği terminalla ödəmək istəyirsinizsə, Terminal inputuna tam məbləği yazın. Bu halda Nağd 0 olacaq.
                      </Typography.Paragraph>

                      <Divider style={{ margin: '12px 0' }} />

                      <Typography.Title level={5} style={{ marginBottom: 12 }}>
                        💸 Alınan məbləğ
                      </Typography.Title>
                      <Typography.Paragraph style={{ marginBottom: 0 }}>
                        Müştəridən əlinizə <strong>alınan məbləği</strong> daxil edin. Sistem avtomatik olaraq müştəriyə <strong>qaytarılmalı qalıq məbləği</strong> hesablayıb göstərəcək.
                      </Typography.Paragraph>
                    </div>
                  }
                  title='Ödəniş izahı'
                  trigger='hover'
                  placement='bottomLeft'
                >
                  <QuestionCircleOutlined style={{ fontSize: 16, color: '#1890ff', cursor: 'pointer' }} />
                </Popover>
              </Space>
            }
            bodyStyle={{ padding: 12 }}
          >
            <Row gutter={12}>
              <Col xs={24} sm={8}>
                <TextField
                  name='cashAmount'
                  format='decimal'
                  item={{ label: 'Nağd 💵', style: { marginBottom: 8 } }}
                  input={{
                    placeholder: '0.00',
                    suffix: CurrencySymbols.AZN,
                    disabled: true,
                    size: 'middle',
                    style: {
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: 600,
                      padding: '8px 12px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      border: '2px solid #e0e0e0',
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <TextField
                  name='terminalAmount'
                  format='decimal'
                  item={{ label: 'Terminal 💳', style: { marginBottom: 8 } }}
                  input={{
                    placeholder: '0.00',
                    suffix: CurrencySymbols.AZN,
                    disabled: values.sync === 'terminal',
                    size: 'middle',
                    style: {
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: 600,
                      padding: '8px 12px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      border: '2px solid #e0e0e0',
                    },
                  }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <TextField
                  name='amountToBeBorrowed'
                  format='decimal'
                  item={{ label: 'Alınan məbləğ 💸', style: { marginBottom: 8 } }}
                  input={{
                    placeholder: '0.00',
                    suffix: CurrencySymbols.AZN,
                    size: 'middle',
                    style: {
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: 600,
                      padding: '8px 12px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      border: '2px solid #e0e0e0',
                    },
                  }}
                />
              </Col>
            </Row>

            {amountToBeGivenBack.amount !== 0 && (
              <Alert
                message='Qaytarılmalı məbləğ'
                description={
                  <Typography.Text strong style={{ fontSize: 15, color: amountToBeGivenBack.color }}>
                    {amountToBeGivenBack.amount.toFixed(2)} {CurrencySymbols.AZN}
                  </Typography.Text>
                }
                type={amountToBeGivenBack.amount > 0 ? 'success' : 'warning'}
                showIcon
                style={{ marginTop: 8 }}
              />
            )}
          </Card>

          {/* Əməliyyat Seçimləri */}
          <Card
            size='small'
            title={
              <Typography.Text strong style={{ fontSize: 14 }}>
                Əməliyyatlar
              </Typography.Text>
            }
            bodyStyle={{ padding: 12 }}
          >
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Space direction='vertical' size='small' style={{ width: '100%' }}>
                  <CheckboxField
                    name='handover'
                    input={{
                      children: '✓ Təhvil ver',
                      disabled: !values.handoverPreparations,
                    }}
                  />
                  <CheckboxField name='handoverPreparations' input={{ children: '🏬 Anbara yönəlt' }} />
                  <CheckboxField name='redirectToBalance' input={{ children: '💰 Balans artır' }} />
                </Space>

                {/* Avtomatik çap mesajı */}
                {!values.handoverPreparations && <Alert message='Təhvil sənədi avtomatik çap ediləcək' type='info' showIcon style={{ marginTop: 12, fontSize: 12 }} />}
              </Col>

              {/* Paket Sayğacları */}
              <Col xs={24} md={12}>
                <div style={{ backgroundColor: '#fafafa', padding: '8px 12px', borderRadius: '4px' }}>
                  <Typography.Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                    Paket Sayı
                  </Typography.Text>
                  <PackageCounter label='Böyük' name='big_package' value={values.big_package} onIncrement={() => onIncrement('big_package')} onDecrement={() => onDecrement('big_package')} />
                  <PackageCounter
                    label='Orta'
                    name='medium_package'
                    value={values.medium_package}
                    onIncrement={() => onIncrement('medium_package')}
                    onDecrement={() => onDecrement('medium_package')}
                  />
                  <PackageCounter label='Kiçik' name='small_package' value={values.small_package} onIncrement={() => onIncrement('small_package')} onDecrement={() => onDecrement('small_package')} />
                </div>
              </Col>
            </Row>
          </Card>
        </AntForm>
      </Space>
    </Modal>
  );
};

export const HandoverDeclarations: FC = () => {
  const { initialValues, onSubmit } = useHandoverDeclarations();

  if (!initialValues) {
    return null;
  }

  return <Formik initialValues={initialValues} enableReinitialize={true} onSubmit={onSubmit} component={FormikComponent} />;
};
